"use client";
import { usePageBreadcrumbs } from "@/components/layout/breadcrumb-context";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useAuthProfile } from "@/hooks/use-auth-profile";
import { useRecipeBoxes } from "@/hooks/use-recipe-boxes";
import { useToast } from "@/hooks/use-toast";
import { canCreateRecipe, getTierDisplayInfo } from "@/lib/account-tier-service";
import { trackRecipeCreated, trackRecipeImported } from "@/lib/analytics-service";
import { getClientFunctions } from "@/lib/firebase-client";
import { createRecipe } from "@/lib/recipe-service";
import { httpsCallable } from "firebase/functions";
import {
    AlertCircle,
    BookOpen,
    CheckCircle2,
    ChefHat,
    Clock,
    Edit,
    ExternalLink,
    FileText,
    Image as ImageIcon,
    Link as LinkIcon,
    List,
    Loader2,
    Save,
    Sparkles,
    Tag,
    Users,
} from "lucide-react";
import { type FormEvent, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

type ImportedRecipe = {
  title: string | null;
  image: string | null;
  ingredients: string[];
  instructions: string[] | string | null;
  prepTime?: string | number | null;
  cookTime?: string | number | null;
  totalTime?: string | number | null;
  servings?: string | number | null;
  author?: string | null;
  sourceUrl: string;
  importedAt?: unknown;
  createdBy?: string | null;
};

type ImportRecipeResponse = {
  success: boolean;
  recipe: ImportedRecipe;
};

const formatMaybeNumber = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined) {
    return "";
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? String(value) : "";
  }

  return value.trim();
};

const normalizeInstructions = (instructions: string[] | string | null | undefined): string[] => {
  if (!instructions) {
    return [];
  }

  if (Array.isArray(instructions)) {
    return instructions.reduce<string[]>((acc, step) => {
      const trimmed = step?.trim();
      if (trimmed) {
        acc.push(trimmed);
      }
      return acc;
    }, []);
  }

  return instructions
    .split(/\r?\n+/)
    .map((step) => step.trim())
    .filter(Boolean);
};

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  if (
    error &&
    typeof error === "object" &&
    "message" in error &&
    typeof (error as { message?: unknown }).message === "string"
  ) {
    return (error as { message: string }).message;
  }

  return "Unable to import recipe. Please try a different URL.";
};

type AddRecipeContainerProps = Readonly<{
  defaultRecipeBoxId?: string;
}>;

export function AddRecipeContainer({ defaultRecipeBoxId }: AddRecipeContainerProps) {
  usePageBreadcrumbs([
    { label: "Recipe Boxes", href: "/home" },
    { label: "Add Recipe" },
  ]);

  const [importUrl, setImportUrl] = useState("");
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importedRecipe, setImportedRecipe] = useState<ImportedRecipe | null>(null);
  const [activeTab, setActiveTab] = useState("import");
  const [selectedRecipeBoxId, setSelectedRecipeBoxId] = useState<string>(defaultRecipeBoxId ?? "");
  const [title, setTitle] = useState("");
  const [ingredientsText, setIngredientsText] = useState("");
  const [instructionsText, setInstructionsText] = useState("");
  const [notesText, setNotesText] = useState("");
  const [labelsText, setLabelsText] = useState("");
  const [prepTime, setPrepTime] = useState("");
  const [cookTime, setCookTime] = useState("");
  const [servings, setServings] = useState("");
  const [recipeImageFile, setRecipeImageFile] = useState<File | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveProgress, setSaveProgress] = useState(0);
  const [saveStatus, setSaveStatus] = useState("");
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user, profile, loading: authLoading } = useAuthProfile();
  const ownerUid = user?.uid ?? null;
  const { boxes, loading: boxesLoading, error: boxesError } = useRecipeBoxes(ownerUid);
  const [clientFunctions, setClientFunctions] = useState(() => getClientFunctions());

  useEffect(() => {
    if (!clientFunctions) {
      setClientFunctions(getClientFunctions());
    }
  }, [clientFunctions]);

  const importRecipeCallable = useMemo(
    () => (clientFunctions ? httpsCallable<{ url: string }, ImportRecipeResponse>(clientFunctions, "importRecipe") : null),
    [clientFunctions],
  );

  const selectedRecipeBox = useMemo(
    () => boxes.find((box) => box.id === selectedRecipeBoxId) ?? null,
    [boxes, selectedRecipeBoxId],
  );

  const previewIngredients = useMemo(
    () => importedRecipe?.ingredients ?? [],
    [importedRecipe],
  );

  const previewInstructions = useMemo(
    () => normalizeInstructions(importedRecipe?.instructions),
    [importedRecipe],
  );

  const importSourceHostname = useMemo(() => {
    if (!importedRecipe?.sourceUrl) {
      return null;
    }

    try {
      return new URL(importedRecipe.sourceUrl).hostname;
    } catch {
      return importedRecipe.sourceUrl;
    }
  }, [importedRecipe?.sourceUrl]);

  const handleImportSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const trimmedUrl = importUrl.trim();

    if (!trimmedUrl) {
      setImportError("Enter a recipe URL to import.");
      return;
    }

    if (!importRecipeCallable) {
      setImportError("Firebase Functions is unavailable in this environment.");
      return;
    }

    setImportError(null);
    setImporting(true);

    try {
      console.log("Starting recipe import for URL:", trimmedUrl);
      const result = await importRecipeCallable({ url: trimmedUrl });
      console.log("Import result received:", result);
      const data = result.data;

      if (!data?.success || !data.recipe) {
        console.error("Import returned invalid data:", data);
        throw new Error("Recipe import failed. Please try again.");
      }

      console.log("Recipe imported successfully:", {
        title: data.recipe.title,
        ingredientCount: data.recipe.ingredients?.length,
        instructionCount: data.recipe.instructions?.length,
      });

      trackRecipeImported("url");

      setImportedRecipe({
        ...data.recipe,
        ingredients: data.recipe.ingredients ?? [],
        instructions: data.recipe.instructions ?? [],
      });
      setImportUrl(data.recipe.sourceUrl ?? trimmedUrl);

      toast({
        title: "Recipe imported",
        description: "Review the preview, then save or tweak before publishing.",
      });
    } catch (error) {
      console.error("Failed to import recipe", error);
      console.error("Error details:", {
        message: error instanceof Error ? error.message : String(error),
        code: (error as { code?: unknown })?.code,
        details: (error as { details?: unknown })?.details,
        stack: error instanceof Error ? error.stack : undefined,
      });
      const message = getErrorMessage(error);
      setImportedRecipe(null);
      setImportError(message);
      toast({
        variant: "destructive",
        title: "Import failed",
        description: message,
      });
    } finally {
      setImporting(false);
    }
  };

  const handleUseImportedInEditor = () => {
    setActiveTab("manual");
  };

  useEffect(() => {
    if (!importedRecipe) {
      return;
    }

    setTitle(importedRecipe.title ?? "");
    setIngredientsText((importedRecipe.ingredients ?? []).join("\n"));
    setInstructionsText(previewInstructions.join("\n\n"));
    setRecipeImageFile(null);
    setPrepTime(formatMaybeNumber(importedRecipe.prepTime));
    setCookTime(formatMaybeNumber(importedRecipe.cookTime));
    setServings(formatMaybeNumber(importedRecipe.servings));
    setNotesText(importedRecipe.sourceUrl ? `Imported from ${importedRecipe.sourceUrl}` : "");
    setLabelsText("");
    setSaveError(null);

  }, [importedRecipe, previewInstructions]);

  const previewImageUrl = useMemo(() => {
    if (recipeImageFile) {
      return URL.createObjectURL(recipeImageFile);
    }
    return importedRecipe?.image ?? null;
  }, [importedRecipe, recipeImageFile]);

  useEffect(() => {
    if (!recipeImageFile || !previewImageUrl) {
      return;
    }
    return () => {
      URL.revokeObjectURL(previewImageUrl);
    };
  }, [previewImageUrl, recipeImageFile]);

  useEffect(() => {
    if (defaultRecipeBoxId) {
      setSelectedRecipeBoxId(defaultRecipeBoxId);
      return;
    }

    if (!selectedRecipeBoxId && boxes.length > 0) {
      setSelectedRecipeBoxId(boxes[0]?.id ?? "");
    }
  }, [boxes, defaultRecipeBoxId, selectedRecipeBoxId]);

  const handleSave = async () => {
    if (authLoading) {
      return;
    }

    if (!user) {
      setSaveError("You need to be signed in to save recipes.");
      return;
    }

    if (!selectedRecipeBoxId) {
      setSaveError("Please choose a recipe box.");
      return;
    }

    if (!title.trim()) {
      setSaveError("Give your recipe a title before saving.");
      return;
    }

    const ingredients = ingredientsText
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);

    if (ingredients.length === 0) {
      setSaveError("Add at least one ingredient.");
      return;
    }

    const directions = instructionsText.trim();
    if (!directions) {
      setSaveError("Provide directions so others know how to make this recipe.");
      return;
    }

    // Check account tier limits
    const accountTier = profile?.accountTier ?? "free";
    const totalRecipeCount = boxes.reduce((sum, box) => sum + (box.recipeCount || 0), 0);
    
    if (!canCreateRecipe(accountTier, totalRecipeCount)) {
      const tierInfo = getTierDisplayInfo(accountTier);
      const maxRecipes = tierInfo.maxRecipesPerBox === null ? "unlimited" : tierInfo.maxRecipesPerBox;
      setSaveError(
        `You've reached your recipe limit (${maxRecipes} recipes) for ${tierInfo.name}. ` +
        "Please upgrade your account to create more recipes."
      );
      return;
    }

    setSaveError(null);
    setIsSaving(true);
    setSaveProgress(0);
    setSaveStatus("Starting...");

    try {
      await createRecipe(
        user.uid,
        {
          recipeBoxId: selectedRecipeBoxId,
          title,
          labels: labelsText
            .split(",")
            .map((label) => label.trim())
            .filter(Boolean),
          ingredients,
          directions,
          notes: notesText.trim() || undefined,
          prepTime: prepTime.trim() || undefined,
          cookTime: cookTime.trim() || undefined,
          servings: servings.trim() || undefined,
          imageFile: recipeImageFile,
          imageHint: selectedRecipeBox?.imageHint ?? title,
          sourceImageUrl: recipeImageFile ? undefined : importedRecipe?.image ?? undefined,
        },
        // Progress callback
        (status, progress) => {
          setSaveStatus(status);
          setSaveProgress(progress);
        }
      );

      trackRecipeCreated(!!recipeImageFile || !!importedRecipe?.image, undefined);

      toast({
        title: "Recipe saved",
        description: "Your recipe is now available in the selected recipe box.",
      });

      navigate(`/recipe-box/${selectedRecipeBoxId}`);
    } catch (error) {
      console.error("Failed to save recipe", error);
      setSaveError("Could not save this recipe. Please try again.");
    } finally {
      setIsSaving(false);
      setSaveProgress(0);
      setSaveStatus("");
    }
  };

  if (authLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="font-headline">Preparing recipe workspace…</CardTitle>
          <CardDescription>Hang tight while we load your profile.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="h-10 w-full animate-pulse rounded-md bg-muted" />
            <div className="h-10 w-full animate-pulse rounded-md bg-muted" />
            <div className="h-32 w-full animate-pulse rounded-md bg-muted" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!user) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Sign in required</AlertTitle>
        <AlertDescription>
          Sign in to scrape recipes and save them to your recipe boxes.
        </AlertDescription>
      </Alert>
    );
  }

  if (boxesError) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Unable to load recipe boxes</AlertTitle>
        <AlertDescription>{boxesError}</AlertDescription>
      </Alert>
    );
  }

  if (!boxesLoading && boxes.length === 0) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>No recipe boxes yet</AlertTitle>
        <AlertDescription>
          Create a recipe box first, then come back to save your recipes.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="import" className="flex items-center gap-2">
            <LinkIcon className="h-4 w-4" />
            Import from URL
          </TabsTrigger>
          <TabsTrigger value="manual" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Create Manually
          </TabsTrigger>
        </TabsList>
        <TabsContent value="import" className="space-y-6">
          <Card className="transition-all hover:shadow-lg hover:border-primary/20">
            <CardHeader>
              <CardTitle className="font-headline flex items-center gap-2">
                <LinkIcon className="h-5 w-5 text-primary" />
                Import Recipe from URL
              </CardTitle>
              <CardDescription>
                Paste the URL of a recipe webpage and we&apos;ll extract the title, ingredients, and steps for you.
              </CardDescription>
            </CardHeader>
          <CardContent className="space-y-4">
            <form className="space-y-4" onSubmit={handleImportSubmit}>
              <div className="space-y-2">
                <Label htmlFor="import-url" className="flex items-center gap-2">
                  <LinkIcon className="h-4 w-4" />
                  Recipe URL
                </Label>
                <Input
                  id="import-url"
                  type="url"
                  placeholder="https://www.seriouseats.com/classic-beef-chili-recipe"
                  value={importUrl}
                  onChange={(event) => setImportUrl(event.target.value)}
                  disabled={importing}
                  required
                  className="transition-all focus:ring-2 focus:ring-primary/20"
                />
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <Button type="submit" disabled={importing} className="transition-all hover:scale-105">
                  {importing ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Sparkles className="mr-2 h-4 w-4" />
                  )}
                  {importing ? "Importing…" : "Import recipe"}
                </Button>
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" />
                  Works with popular sites like Serious Eats, NYT Cooking, BBC Good Food, and more.
                </p>
              </div>
            </form>

            {importError ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Unable to import</AlertTitle>
                <AlertDescription>{importError}</AlertDescription>
              </Alert>
            ) : null}
          </CardContent>
        </Card>

        {importedRecipe ? (
          <Card className="mt-6 transition-all hover:shadow-lg hover:border-primary/20">
            <CardHeader>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <CardTitle className="font-headline text-2xl flex items-center gap-2">
                    <ChefHat className="h-6 w-6 text-primary" />
                    {importedRecipe.title ?? "Imported recipe"}
                  </CardTitle>
                  <CardDescription className="flex items-center gap-1 mt-1">
                    <BookOpen className="h-3 w-3" />
                    {importSourceHostname ? `Imported from ${importSourceHostname}` : "Imported recipe"}
                  </CardDescription>
                </div>
                {importedRecipe.sourceUrl ? (
                  <Button asChild variant="outline" size="sm" className="transition-all hover:scale-105">
                    <a href={importedRecipe.sourceUrl} target="_blank" rel="noreferrer">
                      View original
                      <ExternalLink className="ml-2 h-4 w-4" />
                    </a>
                  </Button>
                ) : null}
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-6 lg:grid-cols-[280px,1fr]">
                <div className="space-y-4">
                  {importedRecipe.image ? (
                    <img
                      src={importedRecipe.image}
                      alt={importedRecipe.title ?? "Imported recipe"}
                      className="aspect-[4/3] w-full rounded-md object-cover"
                    />
                  ) : (
                    <div className="flex aspect-[4/3] w-full items-center justify-center rounded-md border border-dashed">
                      <p className="text-sm text-muted-foreground">No image provided</p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="rounded-md border bg-muted/40 p-3 transition-all hover:bg-muted/60 hover:border-primary/20 hover:shadow-sm">
                      <p className="font-medium flex items-center gap-2">
                        <Clock className="h-4 w-4 text-primary" />
                        Prep time
                      </p>
                      <p className="text-muted-foreground mt-1">
                        {formatMaybeNumber(importedRecipe.prepTime) || "—"}
                      </p>
                    </div>
                    <div className="rounded-md border bg-muted/40 p-3 transition-all hover:bg-muted/60 hover:border-primary/20 hover:shadow-sm">
                      <p className="font-medium flex items-center gap-2">
                        <Clock className="h-4 w-4 text-primary" />
                        Cook time
                      </p>
                      <p className="text-muted-foreground mt-1">
                        {formatMaybeNumber(importedRecipe.cookTime) || "—"}
                      </p>
                    </div>
                    <div className="rounded-md border bg-muted/40 p-3 transition-all hover:bg-muted/60 hover:border-primary/20 hover:shadow-sm">
                      <p className="font-medium flex items-center gap-2">
                        <Clock className="h-4 w-4 text-primary" />
                        Total time
                      </p>
                      <p className="text-muted-foreground mt-1">
                        {formatMaybeNumber(importedRecipe.totalTime) || "—"}
                      </p>
                    </div>
                    <div className="rounded-md border bg-muted/40 p-3 transition-all hover:bg-muted/60 hover:border-primary/20 hover:shadow-sm">
                      <p className="font-medium flex items-center gap-2">
                        <Users className="h-4 w-4 text-primary" />
                        Servings
                      </p>
                      <p className="text-muted-foreground mt-1">
                        {formatMaybeNumber(importedRecipe.servings) || "—"}
                      </p>
                    </div>
                  </div>

                  {importedRecipe.author ? (
                    <p className="text-sm text-muted-foreground">
                      Original author: <span className="font-medium text-foreground">{importedRecipe.author}</span>
                    </p>
                  ) : null}
                </div>

                <div className="grid gap-6 md:grid-cols-2">
                  <div className="space-y-3">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      <List className="h-5 w-5 text-primary" />
                      Ingredients
                    </h3>
                    {previewIngredients.length > 0 ? (
                      <ul className="space-y-2 text-sm">
                        {previewIngredients.map((ingredient, index) => (
                          <li
                            key={`${ingredient}-${index}`}
                            className="rounded-md border bg-muted/40 p-2 transition-all hover:bg-muted/60 hover:border-primary/20 hover:shadow-sm"
                          >
                            {ingredient}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-muted-foreground">No ingredients found.</p>
                    )}
                  </div>
                  <div className="space-y-3">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      <BookOpen className="h-5 w-5 text-primary" />
                      Directions
                    </h3>
                    {previewInstructions.length > 0 ? (
                      <ol className="space-y-2 text-sm">
                        {previewInstructions.map((step, index) => (
                          <li
                            key={`${index}-${step.slice(0, 16)}`}
                            className="flex items-start gap-3 rounded-md border bg-muted/40 p-3 transition-all hover:bg-muted/60 hover:border-primary/20 hover:shadow-sm"
                          >
                            <span className="font-semibold text-muted-foreground">{index + 1}.</span>
                            <span>{step}</span>
                          </li>
                        ))}
                      </ol>
                    ) : (
                      <p className="text-sm text-muted-foreground">No directions found.</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-4 border-t pt-6 md:flex-row md:items-center md:justify-between">
                <div className="w-full md:max-w-sm">
                  <Label htmlFor="import-recipe-box" className="flex items-center gap-2">
                    <BookOpen className="h-4 w-4" />
                    Save to recipe box
                  </Label>
                  <Select
                    value={selectedRecipeBoxId}
                    onValueChange={(value) => setSelectedRecipeBoxId(value)}
                    disabled={boxesLoading}
                  >
                    <SelectTrigger id="import-recipe-box" className="transition-all hover:border-primary/40">
                      <SelectValue placeholder="Select a recipe box" />
                    </SelectTrigger>
                    <SelectContent>
                      {boxes.map((box) => (
                        <SelectItem key={box.id} value={box.id}>
                          {box.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <Button
                    variant="outline"
                    onClick={handleUseImportedInEditor}
                    className="transition-all hover:scale-105"
                  >
                    <Edit className="mr-2 h-4 w-4" />
                    Edit before saving
                  </Button>
                  <Button
                    type="button"
                    onClick={handleSave}
                    disabled={isSaving || boxesLoading}
                    className="transition-all hover:scale-105"
                  >
                    {isSaving ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="mr-2 h-4 w-4" />
                    )}
                    {isSaving ? "Saving…" : "Save to recipe box"}
                  </Button>
                </div>
              </div>

              {saveError ? (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Unable to save</AlertTitle>
                  <AlertDescription>{saveError}</AlertDescription>
                </Alert>
              ) : null}
            </CardContent>
          </Card>
        ) : null}
      </TabsContent>
      <TabsContent value="manual" className="space-y-6">
          <Card className="transition-all hover:shadow-lg hover:border-primary/20">
            <CardHeader>
              <CardTitle className="font-headline flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                Recipe Details
              </CardTitle>
              <CardDescription>
                Fill in the details for your new recipe.
              </CardDescription>
            </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="recipe-box" className="flex items-center gap-2">
                  <BookOpen className="h-4 w-4" />
                  Recipe box
                </Label>
                <Select
                  value={selectedRecipeBoxId}
                  onValueChange={(value) => {
                    setSelectedRecipeBoxId(value);
                  }}
                  disabled={boxesLoading}
                >
                  <SelectTrigger id="recipe-box" className="transition-all hover:border-primary/40">
                    <SelectValue placeholder="Select a recipe box" />
                  </SelectTrigger>
                  <SelectContent>
                    {boxes.map((box) => (
                      <SelectItem key={box.id} value={box.id}>
                        {box.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div>
                  <Label htmlFor="prep-time" className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Prep time
                  </Label>
                  <Input
                    id="prep-time"
                    placeholder="e.g., 20 minutes"
                    value={prepTime}
                    onChange={(event) => setPrepTime(event.target.value)}
                    className="transition-all focus:ring-2 focus:ring-primary/20"
                  />
                </div>
                <div>
                  <Label htmlFor="cook-time" className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Cook time
                  </Label>
                  <Input
                    id="cook-time"
                    placeholder="e.g., 45 minutes"
                    value={cookTime}
                    onChange={(event) => setCookTime(event.target.value)}
                    className="transition-all focus:ring-2 focus:ring-primary/20"
                  />
                </div>
                <div>
                  <Label htmlFor="servings" className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Servings
                  </Label>
                  <Input
                    id="servings"
                    placeholder="e.g., 6 servings"
                    value={servings}
                    onChange={(event) => setServings(event.target.value)}
                    className="transition-all focus:ring-2 focus:ring-primary/20"
                  />
                </div>
              </div>
            </div>

            <div className="grid gap-6 md:grid-cols-3">
              <div className="md:col-span-2 space-y-4">
                <div>
                  <Label htmlFor="title" className="flex items-center gap-2">
                    <ChefHat className="h-4 w-4" />
                    Title
                  </Label>
                  <Input
                    id="title"
                    placeholder="e.g., Classic Apple Pie"
                    value={title}
                    onChange={(event) => setTitle(event.target.value)}
                    className="transition-all focus:ring-2 focus:ring-primary/20"
                  />
                </div>

                <div>
                  <Label htmlFor="labels" className="flex items-center gap-2">
                    <Tag className="h-4 w-4" />
                    Labels
                  </Label>
                  <Input
                    id="labels"
                    placeholder="Dessert, Holiday, Family Favorite"
                    value={labelsText}
                    onChange={(event) => setLabelsText(event.target.value)}
                    className="transition-all focus:ring-2 focus:ring-primary/20"
                  />
                  <p className="text-xs text-muted-foreground">Separate labels with commas.</p>
                </div>

                <div>
                  <Label htmlFor="ingredients" className="flex items-center gap-2">
                    <List className="h-4 w-4" />
                    Ingredients
                  </Label>
                  <Textarea
                    id="ingredients"
                    rows={10}
                    placeholder="List each ingredient on a new line."
                    value={ingredientsText}
                    onChange={(event) => setIngredientsText(event.target.value)}
                    className="transition-all focus:ring-2 focus:ring-primary/20"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <Label className="flex items-center gap-2">
                  <ImageIcon className="h-4 w-4" />
                  Recipe Image
                </Label>
                <div className="group relative">
                  {previewImageUrl ? (
                    <img
                      src={previewImageUrl}
                      alt="Recipe"
                      className="aspect-video w-full rounded-md object-cover transition-all group-hover:scale-105 group-hover:shadow-lg"
                    />
                  ) : (
                    <div className="flex aspect-video w-full items-center justify-center rounded-md bg-muted border-2 border-dashed transition-all hover:border-primary/40 hover:bg-muted/80">
                      <div className="text-center">
                        <ImageIcon className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">No image selected</p>
                      </div>
                    </div>
                  )}
                </div>
                <Input
                  id="picture"
                  type="file"
                  accept="image/*"
                  onChange={(event) => {
                    const file = event.target.files?.[0] ?? null;
                    setRecipeImageFile(file);
                  }}
                  className="transition-all hover:border-primary/40"
                />
                <p className="text-xs text-muted-foreground">Upload a JPG, PNG, or GIF up to 5 MB.</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="instructions" className="flex items-center gap-2">
                <BookOpen className="h-4 w-4" />
                Directions
              </Label>
              <Textarea
                id="instructions"
                rows={12}
                placeholder="Provide step-by-step instructions."
                value={instructionsText}
                onChange={(event) => setInstructionsText(event.target.value)}
                className="transition-all focus:ring-2 focus:ring-primary/20"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Notes
              </Label>
              <Textarea
                id="notes"
                rows={4}
                placeholder="Optional tips, variations, or serving suggestions."
                value={notesText}
                onChange={(event) => setNotesText(event.target.value)}
                className="transition-all focus:ring-2 focus:ring-primary/20"
              />
            </div>

            {saveError ? (
              <Alert variant="destructive" className="transition-all">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Unable to save</AlertTitle>
                <AlertDescription>{saveError}</AlertDescription>
              </Alert>
            ) : null}

            <Button
              type="button"
              onClick={handleSave}
              disabled={isSaving || boxesLoading}
              className="transition-all hover:scale-105"
            >
              {isSaving ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              {isSaving ? "Saving..." : "Save Recipe"}
            </Button>
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>

    {/* Progress overlay */}
    {isSaving && saveProgress > 0 && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <Card className="w-96">
          <CardHeader>
            <CardTitle>Saving Recipe</CardTitle>
            <CardDescription>{saveStatus}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                <div 
                  className="h-full bg-primary transition-all duration-300"
                  style={{ width: `${saveProgress}%` }}
                />
              </div>
              <p className="text-center text-sm text-muted-foreground">
                {saveProgress}%
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    )}
    </div>
  );
}
